import { useMemo } from "react";
import { Mountain, TrendingUp, TrendingDown } from "lucide-react";

/**
 * ElevationProfile — SVG sparkline showing the elevation over route length.
 * Props: profile: [{i, elev}], min_elev_m, max_elev_m, elevation_gain_m,
 *        elevation_loss_m
 */
export default function ElevationProfile({
  profile,
  min_elev_m,
  max_elev_m,
  elevation_gain_m,
  elevation_loss_m,
}) {
  const { path, startElev, endElev } = useMemo(() => {
    if (!profile || profile.length < 2) return { path: null, startElev: null, endElev: null };
    const w = 100;
    const h = 40;
    const elevs = profile.map((p) => Number(p.elev)).filter((v) => Number.isFinite(v));
    if (elevs.length < 2) return null;
    const mn = Math.min(...elevs);
    const mx = Math.max(...elevs);
    const span = Math.max(mx - mn, 1);
    const denom = Math.max(profile.length - 1, 1);
    const pts = profile.map((p, idx) => {
      const elev = Number.isFinite(Number(p.elev)) ? Number(p.elev) : mn;
      const x = (idx / denom) * w;
      const y = h - ((elev - mn) / span) * (h - 4) - 2;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    });
    const line = `M ${pts.join(" L ")}`;
    const area = `${line} L ${w},${h} L 0,${h} Z`;
    return {
      path: { line, area },
      startElev: Math.round(elevs[0]),
      endElev: Math.round(elevs[elevs.length - 1]),
    };
  }, [profile]);

  if (!path) return null;

  return (
    <div
      data-testid="elevation-profile"
      className="bg-brema-obsidian/70 border border-white/10 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-brema-yellow">
          <Mountain size={12} /> Profilo Altimetrico
        </div>
        <div className="flex items-center gap-4 font-mono text-[10px] text-zinc-300">
          <span className="flex items-center gap-1" title="Dislivello positivo">
            <TrendingUp size={12} className="text-brema-yellow" />
            <span className="text-white">+{elevation_gain_m ?? 0} m</span>
          </span>
          {elevation_loss_m != null && (
            <span className="flex items-center gap-1" title="Dislivello negativo">
              <TrendingDown size={12} className="text-brema-red" />
              <span className="text-white">−{elevation_loss_m} m</span>
            </span>
          )}
        </div>
      </div>
      <div className="relative">
        <svg
          viewBox="0 0 100 40"
          preserveAspectRatio="none"
          className="w-full h-20"
        >
          <defs>
            <linearGradient id="elevGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#FFD400" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#FFD400" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <path d={path.area} fill="url(#elevGrad)" />
          <path
            d={path.line}
            stroke="#FFD400"
            strokeWidth="0.7"
            fill="none"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        <div className="flex justify-between mt-1 font-mono text-[9px] uppercase tracking-wider text-zinc-400">
          <span title="Quota inizio percorso">START · {startElev != null ? `${startElev} m` : ""}</span>
          <span className="text-zinc-500">
            {min_elev_m != null && max_elev_m != null ? `min ${min_elev_m} · max ${max_elev_m}` : ""}
          </span>
          <span className="text-brema-yellow" title="Quota fine percorso">END · {endElev != null ? `${endElev} m` : ""}</span>
        </div>
      </div>
    </div>
  );
}
